import { loadSession, clearSession, extendSession } from "./session.js";
import type { Figure } from "./figure.js";
import { enqueueAttempt, getQueuedAttempts, removeQueued, chunk } from "./offline-queue.js";
import { cachePackSession, getCachedPackSession } from "./pack-cache.js";
import { reportFunnel, reportError } from "./report-error.js";

/**
 * Tiny fetch wrapper for the API Worker. Adds Authorization on protected
 * routes and handles 401 by clearing the session (which triggers redirect
 * to sign-in via the App router guard).
 */

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

async function fetchJson<T>(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  headers.set("Content-Type", "application/json");

  if (init.auth) {
    const session = loadSession();
    if (!session) {
      throw new ApiError(401, "no_session", "Not signed in");
    }
    headers.set("Authorization", `Bearer ${session.session_id}`);
  }

  // Bound every request so a stalled connection (common on flaky mobile) fails
  // with a network error instead of hanging the screen on "Loading..." forever.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15_000);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...init, headers, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }

  // Sliding session: if the server re-stamped our TTL, slide the local expiry
  // forward so an actively-used session never lapses client-side.
  if (init.auth) {
    const exp = Number(res.headers.get("X-Session-Expires"));
    if (Number.isFinite(exp) && exp > 0) extendSession(exp);
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = {};
  }

  if (!res.ok) {
    if (res.status === 401) {
      clearSession();
      // A session that expired mid-use must not strand the user on a broken
      // screen with no way forward; bounce to sign-in. Guard against a loop
      // when the 401 came from the sign-in/exchange flow itself.
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/signin")) {
        window.location.assign("/signin");
      }
    }
    const err = body as { error?: string; detail?: string };
    throw new ApiError(
      res.status,
      err.error ?? "http_error",
      err.detail ?? `HTTP ${res.status}`,
    );
  }

  return body as T;
}

// --- Auth -----------------------------------------------------

export type ExchangeResponse = {
  session_id: string;
  expires_at: number;
  needs_onboarding: boolean;
  user: {
    id: string;
    email: string;
    name: string | null;
    picture_url: string | null;
  };
};

export function exchangeGoogleToken(id_token: string): Promise<ExchangeResponse> {
  return fetchJson<ExchangeResponse>("/auth/exchange", {
    method: "POST",
    body: JSON.stringify({ id_token }),
  });
}

export function logout(): Promise<{ ok: true }> {
  return fetchJson<{ ok: true }>("/auth/logout", {
    method: "POST",
    auth: true,
  });
}

// --- Attempts -------------------------------------------------

export type Confidence = "LOW" | "MED" | "HIGH";

export type AttemptSubmitBody = {
  id: string; // ULID
  item_id: string;
  snap_latency_ms: number | null;
  total_latency_ms: number;
  /** Letter (A-D) for multiple-choice, or the typed numeric string for grid-in. */
  chosen_answer: string;
  is_correct: boolean;
  chosen_signatures: string[] | null;
  /** Trapline drill: span IDs the user marked as evidence. */
  chosen_evidence_spans?: string[] | null;
  /** Trapline drill: pre-submission confidence self-assessment. */
  confidence?: Confidence | null;
  client_attempted_at: number;
  client: "pwa-ios" | "pwa-android" | "pwa-desktop";
  pack_version: string;
  re_attempt_of?: string | null;
};

export type AttemptSubmitResponse = {
  ok: true;
  attempt_id: string;
  is_correct: boolean | null;
  signature_credit: number | null;
};

/** Transient failures (network / abort / 5xx / 429) are queued for later sync;
 *  permanent ones (bad body 4xx, dead session) are surfaced instead of looping. */
const QUEUE_WORTHY_STATUS = new Set([408, 429, 500, 502, 503, 504]);
export function queueWorthy(err: unknown): boolean {
  if (err instanceof ApiError) return QUEUE_WORTHY_STATUS.has(err.status);
  return true; // raw fetch throw = offline / abort / timeout
}

export function submitAttempt(body: AttemptSubmitBody) {
  // Counted on the ATTEMPT, not on a successful response: an answer the student
  // gave offline still went in, and the outbox will sync it. Also covers the
  // diagnostic and the daily hidden test, which are answer-giving surfaces too;
  // the event means "answered a question outside the practice test".
  if (!answerReported) {
    answerReported = true;
    reportFunnel("drill_first_answer");
  }
  return fetchJson<AttemptSubmitResponse>("/attempts/submit", {
    method: "POST",
    body: JSON.stringify(body),
    auth: true,
  }).then(
    (res) => {
      void flushOutbox(); // we're online — opportunistically drain any backlog
      return res;
    },
    async (err) => {
      if (queueWorthy(err)) {
        // Only claim success if the answer was actually STORED. This used to be
        // `void enqueueAttempt(body)` followed by an unconditional ok:true, so
        // when the write failed — blocked storage, exhausted quota, an aborted
        // transaction — the attempt existed in neither the server nor the
        // outbox, sync could never pick up a row that was never written, and the
        // student saw a normal graded response for an answer that had been
        // thrown away.
        const queued = await enqueueAttempt(body);
        if (!queued) {
          // THE ONE CASE WHERE A STUDENT LOSES AN ANSWER, and until now the only
          // trace of it was a toast on their screen. The network submit failed
          // AND the outbox write failed, so the attempt exists nowhere: not on
          // the server, not in the queue, and nothing will ever retry it.
          //
          // Worth distinguishing from the routine IndexedDB noise on this page,
          // which comes from flushOutbox being torn down mid-drain. That one is
          // harmless, because the queue survives to the next trigger. This one
          // is not, and we could not count it.
          reportError(
            "answer_not_saved",
            err instanceof Error ? err : new Error(String(err)),
          );
          throw err;
        }
        // Resolve optimistically: the ULID is also the server row id, so
        // miss-reason-by-id stays consistent once this syncs.
        return {
          ok: true as const,
          attempt_id: body.id,
          is_correct: body.is_correct,
          signature_credit: null,
        } satisfies AttemptSubmitResponse;
      }
      throw err;
    },
  );
}

const SYNC_BATCH_MAX = 100; // matches SyncBatch.attempts.max on the server
let flushing = false;
/** Drain the offline attempt outbox to /attempts/sync. Safe to call anytime;
 *  single in-flight, idempotent on the server, retries on the next trigger. */
export async function flushOutbox(): Promise<void> {
  if (flushing || !loadSession()) return;
  flushing = true;
  // CATCH, not just finally. Every call site is fire-and-forget
  // (`void flushOutbox()` on load, on `online`, on `visibilitychange`), so
  // anything that escapes here becomes an unhandled rejection on a page the
  // student is working on. Production recorded four of them on /app/drill/slice:
  // "Failed to execute 'transaction' on 'IDBDatabase': The database connection is
  // closing", which happens when the tab is being torn down mid-flush and the
  // cached IDB handle is already closing. Draining the outbox is best-effort by
  // design, and the queue survives to the next trigger, so the correct response
  // to any failure is silence rather than a thrown error.
  try {
    const queued = await getQueuedAttempts();
    for (const batch of chunk(queued, SYNC_BATCH_MAX)) {
      let r: { accepted: string[]; rejected: { id: string; reason: string }[] };
      try {
        r = await fetchJson("/attempts/sync", {
          method: "POST",
          body: JSON.stringify({ attempts: batch }),
          auth: true,
        });
      } catch {
        return; // still offline / 429 / transient — keep the queue, retry later
      }
      // Drop accepted (synced) AND rejected (permanently bad) so we never loop.
      await removeQueued([...r.accepted, ...r.rejected.map((x) => x.id)]);
    }
  } catch {
    // Keep the queue and retry on the next trigger.
  } finally {
    flushing = false;
  }
}

/**
 * The server caps /attempts/recent at 200 rows. Asking for more does not fail,
 * it silently returns fewer, which is how HomeNext ended up with a
 * "plenty logged recently" branch gated on 300 that could never fire.
 */
export const RECENT_ATTEMPTS_MAX = 200;

/**
 * Default lookback for anything shown to a learner as "recent", in days. Wide
 * enough not to truncate a long daily streak; the row cap applies on top.
 */
export const RECENT_WINDOW_DAYS = 90;

/**
 * Latest attempts. `sinceDays` bounds them by AGE, which `limit` cannot do.
 *
 * Pass it whenever the result is shown to the learner as "recent". The server
 * caps limit at 200, so asking for more silently returns fewer, and a plain row
 * count says nothing about when those rows happened.
 */
export function recentAttempts(limit = 50, sinceDays?: number) {
  const window = sinceDays ? `&since_days=${sinceDays}` : "";
  return fetchJson<{ attempts: unknown[] }>(
    `/attempts/recent?limit=${limit}${window}`,
    { auth: true },
  );
}

/** Why a question was missed — tagged optionally at reveal on a wrong answer. */
export type MissReason = "trap" | "careless" | "didnt_know" | "timing";

export function submitMissReason(attemptId: string, reason: MissReason) {
  return fetchJson<{ ok: true }>(`/attempts/${encodeURIComponent(attemptId)}/miss-reason`, {
    method: "POST",
    body: JSON.stringify({ reason }),
    auth: true,
  });
}

export function fetchMissReasons() {
  return fetchJson<{ counts: Record<MissReason, number>; tagged: number }>(
    "/attempts/miss-reasons",
    { auth: true },
  );
}

// --- Mastery --------------------------------------------------

export type MasteryRow = {
  module: string;
  skill_family: string;
  attempts: number;
  /** DISTINCT questions, not attempts. See the query in api/routes/mastery.ts. */
  items: number;
  correct: number;
  mean_snap_ms: number | null;
  last_attempted: number;
};

export function fetchMastery() {
  return fetchJson<{ rows: MasteryRow[] }>("/mastery", { auth: true });
}

// --- Pattern Trace (snap-latency heatmap, E5) ----------------

export type TraceCell = {
  module: string;
  skill_family: string;
  /** floor(attempted_at_ms / 604800000) — a week index. */
  week_bucket: number;
  /** Mean snap latency in ms for this family-week (SQLite has no median). */
  mean_snap_ms: number | null;
  accuracy: number;
  n: number;
};

export function fetchPatternTrace() {
  return fetchJson<{ cells: TraceCell[]; now_bucket: number; weeks: number }>(
    "/mastery/trace",
    { auth: true },
  );
}

export type MistakeBucket = {
  module: string;
  skill_family: string;
  total: number;
  missed: number;
};

export function fetchMistakeBuckets() {
  return fetchJson<{ buckets: MistakeBucket[] }>("/mastery/signatures", {
    auth: true,
  });
}

// Confidence calibration: accuracy at each self-reported confidence level, plus
// the family where the user is most overconfident (felt HIGH, scored low).
export type CalibrationRow = {
  confidence: "LOW" | "MED" | "HIGH";
  attempts: number;
  /** DISTINCT questions, not attempts. See the query in api/routes/mastery.ts. */
  items: number;
  correct: number;
};
export type Calibration = {
  by_confidence: CalibrationRow[];
  overconfidence: {
    module: string;
    skill_family: string;
    attempts: number;
    correct: number;
  } | null;
};

export function fetchCalibration() {
  return fetchJson<Calibration>("/mastery/calibration", { auth: true });
}

// --- Packs ----------------------------------------------------

/**
 * Grammar v2 clause decomposition. Each span is one constituent
 * clause/phrase of the stimulus *as the correct option fills it in*,
 * in surface order. Concatenated (with original whitespace) the
 * `text` values reconstruct `passage_text` exactly, so the span that
 * contains the fill-in slot keeps the literal `_____` marker. `type`
 * is one of the nine grammar-structure tags the drafter emits; see
 * `clauseSpanLabel` for the closed set we render.
 */
export type ClauseSpan = {
  type: string;
  text: string;
};

export type PackItem = {
  item_id: string;
  module: string;
  skill_family: string;
  difficulty_band: number;
  /**
   * "multiple_choice" (default, omitted) or "grid_in" (student-produced numeric
   * response). Grid-in items render a typed input instead of 4 option cards and
   * are graded against `verifier` via gradeGridIn.
   */
  response_format?: "multiple_choice" | "grid_in";
  /**
   * Task-interaction type (Backlog E1 — see docs/PLAN-2026-06-01-task-interaction-layer-E1.md).
   * Absent ⇒ "multiple_choice"; "grid_in" is currently also inferred from
   * response_format. Resolve via resolveTaskType() and grade via gradeTask() (both @bht/schema).
   */
  task_type?:
    | "multiple_choice"
    | "grid_in"
    | "claim_judgment"
    | "weak_evidence"
    | "drag_to_bucket"
    | "ordered_sequence"
    | "hotspot"
    | "label_match";
  /** Grid-in grading data. Present on grid_in items (slice carries it already). */
  verifier?: {
    kind?: string;
    correct_value: string;
    accepted_values?: string[];
    tolerance?: number;
  };
  /**
   * claim_judgment grading data (Claim Check, Backlog E2). The drill renders the
   * verdict choices as options (one is_correct) and reuses MC grading; `claim`
   * carries the canonical verdict for server-authoritative gradeTask parity.
   */
  claim?: { verdict: string; verdicts?: string[] };
  /**
   * Slice attack-lens (Backlog E3 / #64). The precise misread the item is built
   * around: `term` is the exact trap phrase from the problem, `rationale` why it
   * trips solvers. Optional + reveal-only; absent on items without one.
   */
  attack_target?: { term: string; rationale: string };
  /**
   * Slice fastest-path reveal (the Desmos shortcut shown next to the trap). The
   * efficient move a high scorer would actually make, not the long algebraic
   * way. desmos_input is the exact lines to type; correctness of desmos_graph
   * paths is gated offline by scripts/llm/verify-fastest-path.py (SymPy), never
   * by an LLM. Optional + reveal-only.
   */
  fastest_path?: {
    method: "desmos_graph" | "desmos_table" | "algebra" | "plug_in" | "estimate";
    desmos_input?: string[];
    read?: string;
    steps: string;
  };
  /**
   * drag_to_bucket data (Decoder drag-slots + Radar drag-evidence, E3 / #64):
   * the learner places each token into a bucket. tokens[].bucket is the correct
   * bucket id. Graded via gradeTask (partial credit). Rendered with SlotBoard.
   */
  drag?: {
    buckets: { id: string; label: string }[];
    tokens: { id: string; text: string; bucket: string }[];
  };
  /** hotspot (Figure Anatomy, E4): accepted figure-region ids. */
  hotspot?: { answer_region_ids: string[] };
  /**
   * ordered_sequence (Multi-Panel Logic Chain, E4): `correct_order` is the right
   * id order (the only field grading needs); `steps` carries the render labels
   * (id → text) the OrderBoard shows. Submitted value is JSON.stringify(idOrder).
   */
  sequence?: { correct_order: string[]; steps?: { id: string; text: string }[] };
  stimulus: {
    passage_text?: string;
    context?: string;
    sample_response?: string;
    clause_spans?: ClauseSpan[];
    /** Synthetic figure (E4 Data & Figures) rendered by FigureRenderer. */
    figure?: Figure;
    /**
     * Conflicting-Viewpoints / Research-Summary passages (E4 Phase A): 1-3 short
     * labeled passages (e.g. "Scientist 1" / "Study 2") rendered above the
     * question. The interaction stays multiple_choice; this only adds stimulus.
     */
    passages?: { label: string; text: string }[];
  };
  task: { prompt: string; primary_skill: string };
  options: Array<{
    id: string;
    text: string;
    is_correct: boolean;
    distractor_signatures: Array<{
      label: string;
      weight: number;
      rationale: string;
    }>;
  }>;
  answer_key: { correct_option: string };
};

/**
 * Defensive normalization at the data boundary.
 *
 * Served items cross an UNTYPED JSON boundary (R2 pack / D1 row / aggregate);
 * the TypeScript types above are aspirational, not enforced at runtime. Every
 * prod crash we hit (shuffleOptions assuming options.length===4, the
 * `[...distractor_signatures]` spread, humanize() on an undefined skill_family)
 * was the same shape: "a field wasn't what the type promised, and one off-shape
 * value crashed the whole screen." We fix the CLASS here by normalizing once at
 * ingest, so every downstream component can trust the shape — instead of adding
 * a guard at each of ~25 render sites and finding the next one via telemetry.
 *
 * This only DEFAULTS missing/wrong-typed soft fields; it never invents content.
 */
export function normalizeItem(raw: PackItem): PackItem {
  const it = (raw ?? {}) as PackItem;

  // Stimulus: keep the object but make every array a renderer iterates safe.
  // Inline `Array.isArray(x) ? x : []` (not a generic helper) so each field
  // keeps its own element type instead of widening to unknown[].
  const stim = (it.stimulus ?? {}) as PackItem["stimulus"];
  const stimulus: PackItem["stimulus"] = {
    ...stim,
    ...(stim.passages !== undefined
      ? { passages: Array.isArray(stim.passages) ? stim.passages : [] }
      : {}),
    ...(stim.clause_spans !== undefined
      ? { clause_spans: Array.isArray(stim.clause_spans) ? stim.clause_spans : [] }
      : {}),
  };

  // Optional interaction sub-objects: repair the arrays renderers touch ONLY if
  // the interaction is present — never invent one (that would change task_type).
  const drag = it.drag
    ? {
        buckets: Array.isArray(it.drag.buckets) ? it.drag.buckets : [],
        tokens: Array.isArray(it.drag.tokens) ? it.drag.tokens : [],
      }
    : it.drag;
  const sequence = it.sequence
    ? {
        correct_order: Array.isArray(it.sequence.correct_order) ? it.sequence.correct_order : [],
        ...(it.sequence.steps !== undefined
          ? { steps: Array.isArray(it.sequence.steps) ? it.sequence.steps : [] }
          : {}),
      }
    : it.sequence;
  const hotspot = it.hotspot
    ? {
        answer_region_ids: Array.isArray(it.hotspot.answer_region_ids)
          ? it.hotspot.answer_region_ids
          : [],
      }
    : it.hotspot;
  const claim = it.claim
    ? {
        verdict: it.claim.verdict ?? "",
        ...(it.claim.verdicts !== undefined
          ? { verdicts: Array.isArray(it.claim.verdicts) ? it.claim.verdicts : [] }
          : {}),
      }
    : it.claim;
  const verifier = it.verifier
    ? {
        ...it.verifier,
        ...(it.verifier.accepted_values !== undefined
          ? {
              accepted_values: Array.isArray(it.verifier.accepted_values)
                ? it.verifier.accepted_values
                : [],
            }
          : {}),
      }
    : it.verifier;

  return {
    ...it,
    item_id: typeof it.item_id === "string" ? it.item_id : "",
    module: typeof it.module === "string" ? it.module : "",
    skill_family: typeof it.skill_family === "string" ? it.skill_family : "",
    difficulty_band: typeof it.difficulty_band === "number" ? it.difficulty_band : 0,
    task: {
      prompt: it.task?.prompt ?? "",
      primary_skill: it.task?.primary_skill ?? it.skill_family ?? "",
    },
    stimulus,
    answer_key: { correct_option: it.answer_key?.correct_option ?? "" },
    options: (Array.isArray(it.options) ? it.options : []).map((o) => ({
      id: o?.id ?? "",
      text: o?.text ?? "",
      is_correct: !!o?.is_correct,
      distractor_signatures: Array.isArray(o?.distractor_signatures)
        ? o.distractor_signatures
        : [],
    })),
    ...(drag !== undefined ? { drag } : {}),
    ...(sequence !== undefined ? { sequence } : {}),
    ...(hotspot !== undefined ? { hotspot } : {}),
    ...(claim !== undefined ? { claim } : {}),
    ...(verifier !== undefined ? { verifier } : {}),
  };
}

/**
 * Normalize the `items` array of any response that carries one, leaving every
 * other response (and union variants without items) untouched. Applied to all
 * item-serving loaders below.
 */
function normalizeResponse<T>(r: T): T {
  const anyR = r as { items?: unknown };
  if (Array.isArray(anyR.items)) {
    return { ...r, items: anyR.items.map(normalizeItem) };
  }
  return r;
}

export type PackSession = {
  pack_id: string;
  module: string;
  items: PackItem[];
  selection: {
    mode?: "normal" | "weakness" | "review";
    from_weak_families: string[];
    excluded_recently_seen: number;
    /** Weakness mode requested but the user has no weak-spot data yet (cold start). */
    weakness_fallback?: boolean;
    /** Review mode requested but nothing is due yet (cold start). */
    review_fallback?: boolean;
    /** Review mode: how many items were due. */
    review_due?: number;
  };
};

/**
 * Fetch a drill session. mode="weakness" (Backlog E5) returns a deliberate-practice
 * set: strictly weak-family + recently-missed items, re-serving the misses.
 */
/**
 * Fired once per page for the drill funnel. Instrumenting the ROUTES was tried and
 * drifted: `drill_opened` lived in Drill.tsx only, which serves the generic
 * /app/drill/:module catch-all, so the six drills students actually use
 * (trapline, grammar, slice, prompt, science, rubric) reported nothing at all.
 * Four events in four days looked exactly like "nobody opens a drill" and was
 * really "six of seven surfaces are not wired".
 *
 * These two live at the CHOKE POINTS instead. Every drill loads its items through
 * fetchPackSession and sends every answer through submitAttempt, so a new drill
 * route is instrumented the moment it works at all, and cannot be forgotten.
 */
let openedReported = false;
let answerReported = false;

export function fetchPackSession(
  module: string,
  size = 5,
  mode?: "weakness" | "review" | "m1",
) {
  if (!openedReported) {
    openedReported = true;
    reportFunnel("drill_opened");
  }
  const q = mode ? `&mode=${mode}` : "";
  return fetchJson<PackSession>(`/packs/${module}/session?size=${size}${q}`, {
    auth: true,
  })
    .then(normalizeResponse)
    .then((session) => {
      // Cache normal-mode sessions so the drill still loads offline. Weakness/
      // review depend on live server state, so we neither cache nor fall back
      // for them.
      if (!mode) void cachePackSession(module, session);
      return session;
    })
    .catch(async (e) => {
      if (!mode && queueWorthy(e)) {
        const cached = await getCachedPackSession(module);
        if (cached) return cached; // offline: serve the last cached session
      }
      throw e;
    });
}

/**
 * The server-honored drill mode from the current URL. "speed" is a client-only
 * overlay (the server doesn't select differently), so it maps to undefined here.
 */
export function drillMode(): "weakness" | "review" | "m1" | undefined {
  const m = new URLSearchParams(window.location.search).get("mode");
  return m === "weakness" || m === "review" || m === "m1" ? m : undefined;
}

/** Spaced-repetition due counts for the Home review nudge (E5). */
export function fetchReviewDue() {
  return fetchJson<{ total: number; by_module: Record<string, number> }>(
    "/packs/review/due",
    { auth: true },
  );
}

// --- Hidden Test Score ----------------------------------------

export type HtsTrendPoint = { date: string; score: number };

export type HtsScore = {
  total: number;
  correctness: number;
  signature_credit: number;
  speed_bonus: number;
};

export type HtsTodayLocked = {
  unlocked: false;
  attempts_needed: number;
  threshold: number;
  trend: HtsTrendPoint[];
  today: string;
};

export type HtsTodayCompleted = {
  unlocked: true;
  completed: true;
  today: string;
  score: HtsScore;
  completed_at: number;
  item_ids: string[];
  trend: HtsTrendPoint[];
};

export type HtsTodayOpen = {
  unlocked: true;
  completed: false;
  today: string;
  items: PackItem[];
  trend: HtsTrendPoint[];
  pack_id: string;
};

export type HtsToday = HtsTodayLocked | HtsTodayCompleted | HtsTodayOpen;

export function fetchHiddenTestToday() {
  return fetchJson<HtsToday>("/hidden-test/today", { auth: true }).then(
    normalizeResponse,
  );
}

export type HtsCompleteResponse = {
  ok: true;
  already_completed: boolean;
  score: HtsScore;
};

export function completeHiddenTest(body: {
  item_ids: string[];
  attempt_ids: string[];
}) {
  return fetchJson<HtsCompleteResponse>("/hidden-test/complete", {
    method: "POST",
    body: JSON.stringify(body),
    auth: true,
  });
}

// --- Me / Profile / Test Target ----------------------------------

/**
 * PSAT is its own target, not a flavor of SAT.
 *
 * A sophomore sitting the PSAT next month says "PSAT", and asking them to
 * declare SAT instead is asking them to know the two are related. What they
 * study IS the SAT content, because the skills are the same, and
 * targetModuleSections encodes exactly that: a PSAT target yields the SAT
 * modules.
 */
export type ExamTarget = "SAT" | "PSAT" | "ACT" | "AP" | "APPS";
export type ScoreBand = "1200+" | "1300+" | "1400+" | "1500+" | "1550+";

export type Me = {
  id: string;
  email: string;
  name: string | null;
  picture_url: string | null;
  target_exams: ExamTarget[];
  target_band: ScoreBand | null;
  target_date: string | null; // 'YYYY-MM'
  onboarded_at: number | null;
  needs_onboarding: boolean;
  created_at: number;
  is_admin: boolean;
  /** Seeded TEST account (migration 0027); excluded from /admin/stats. */
  is_test?: boolean;
};

export function fetchMe() {
  return fetchJson<Me>("/me", { auth: true });
}

/** Persist today's study-goal progress and get back the consecutive-met-day streak. */
export function recordDailyGoal(body: { day: string; goal: number; done: number }) {
  return fetchJson<{ streak: number; met: number; done: number; goal: number }>(
    "/me/daily-goal",
    { method: "POST", body: JSON.stringify(body), auth: true },
  );
}

// --- Admin (operator-only; server gates via requireAdmin) ------

export type AdminStats = {
  computed_at: number;
  users: {
    total: number;
    new_7d: number;
    new_30d: number;
    onboarded: number;
    dau: number;
    wau: number;
    mau: number;
  };
  activity: {
    total_attempts: number;
    attempts_24h: number;
    attempts_7d: number;
    attempts_30d: number;
    attempts_per_active_user: number;
  };
  per_module: { module: string; attempts: number }[];
  practice_tests: { started: number; completed: number };
  funnel: {
    signed_up: number;
    onboarded: number;
    active: number;
    took_practice_test: number;
  };
  cache: "hit" | "miss";
};

export function fetchAdminStats() {
  return fetchJson<AdminStats>("/admin/stats", { auth: true });
}

export type AdminFlag = {
  item_id: string;
  user_id: string;
  attempt_id: string;
  flag_type: string;
  created_at: number;
};

export type AdminFlagsResponse = {
  counts_by_type: Record<string, number>;
  /** Reports already acted on. Hidden from the list; surfaced as a count so it
      is visible that they were closed rather than lost. */
  resolved_total: number;
  flags: AdminFlag[];
  limit: number;
  offset: number;
};

export function fetchAdminFlags(limit = 50, offset = 0) {
  return fetchJson<AdminFlagsResponse>(
    `/admin/flags?limit=${limit}&offset=${offset}`,
    { auth: true },
  );
}

export type AdminTelemetryError = {
  created_at: number;
  kind: string;
  name: string | null;
  message: string | null;
  route: string | null;
  user_agent: string | null;
  signed_in: number;
  /** Entry-chunk hash of the build that produced this report; null before 2026-09-10. */
  build: string | null;
};

export type AdminTelemetryResponse = {
  /** OPEN errors. Resolved ones are excluded so a new failure is not the 47th row. */
  error_total: number;
  resolved_total: number;
  errors: AdminTelemetryError[];
  funnel: Array<{ name: string; n: number }>;
  /**
   * Builds that reported anything in the last 7 days, most recent first. An
   * error whose `build` is not the first entry came from a tab running an older
   * bundle, which is the difference between a live regression and a client that
   * has not reloaded — a distinction that previously required grepping the
   * deployed chunk by hand.
   */
  builds: Array<{ build: string; n: number; last_seen: number }>;
  limit: number;
};

export function fetchAdminTelemetry(limit = 50) {
  return fetchJson<AdminTelemetryResponse>(`/admin/telemetry?limit=${limit}`, {
    auth: true,
  });
}

export type AdminFeedback = {
  id: string;
  user_id: string | null;
  created_at: number;
  category: string;
  message: string;
  path: string | null;
  item_id: string | null;
  client: string | null;
  user_agent: string | null;
};

export type AdminFeedbackResponse = {
  total: number;
  /** Answered feedback, hidden from the queue but counted. */
  answered_total: number;
  feedback: AdminFeedback[];
  limit: number;
  offset: number;
};

export function fetchAdminFeedback(limit = 50, offset = 0) {
  return fetchJson<AdminFeedbackResponse>(
    `/admin/feedback?limit=${limit}&offset=${offset}`,
    { auth: true },
  );
}

export type SetTargetBody = {
  target_exams: ExamTarget[];
  target_band?: ScoreBand | null;
  target_date?: string | null;
};

export type SetTargetResponse = {
  ok: true;
  target_exams: ExamTarget[];
  target_band: ScoreBand | null;
  target_date: string | null;
  onboarding_completed: boolean;
};

export function setTarget(body: SetTargetBody) {
  return fetchJson<SetTargetResponse>("/me/target", {
    method: "POST",
    body: JSON.stringify(body),
    auth: true,
  });
}

// --- Practice Test (Phase 0) ----------------------------------

export type PracticeTestSection =
  | "rw"
  | "math"
  | "english"
  | "reading"
  | "science"
  | "done";

export type PracticeTestSession = {
  id: string;
  user_id: string;
  form_id: string;
  status: "in_progress" | "submitted" | "abandoned";
  started_at: number;
  submitted_at: number | null;
  current_section: PracticeTestSection | null;
  current_module: number | null;
  current_index: number;
  rw_correct: number | null;
  rw_total: number | null;
  math_correct: number | null;
  math_total: number | null;
  timed_mode: boolean;
  module_started_at: number | null;
};

export type PracticeTestForm = {
  form_id: string;
  name: string;
  /** SAT forms carry module timing; ACT forms carry a per-section list instead. */
  timing?: {
    rw_module_minutes: number;
    math_module_minutes: number;
    section_break_minutes: number;
  };
  /** Linear tests (ACT, AP Calculus): the ordered sections + per-section
   * minutes. `calculator` is AP Calculus only, where Part B requires one. */
  sections?: Array<{
    key: string;
    label: string;
    minutes: number;
    operational: number;
    calculator?: boolean;
  }>;
};

export type PracticeTestStateResponse = {
  session: PracticeTestSession;
  /** "act" or "apcalc" for linear sessions; absent/"sat" for the SAT flow. */
  test_type?: "sat" | "act" | "apcalc";
  /** Gauntlet: one timed hard Math module — header shows no "of 2". */
  gauntlet_mode?: boolean;
  form: PracticeTestForm;
  items: PackItem[];
  responses: Array<{
    item_id: string;
    chosen_answer: string | null;
    is_correct: number | null;
    marked_for_review: number;
    position: number;
    time_spent_ms: number | null;
  }>;
  time_remaining_ms: number | null;
  /**
   * The student is at a module whose clock has not started: the section break.
   * Derived server-side from a NULL module_started_at, so it cannot disagree with
   * the clock. Resume carries it so a reload on the break screen stays on it.
   */
  on_break?: boolean;
  /** Break length from the form manifest. Never assume ten. */
  break_minutes?: number | null;
};

/** One routed section's Module-1-vs-threshold coaching stats (ADR 0005 / B2). */
export type SectionRouting = {
  route: "easy" | "hard";
  m1_correct: number;
  m1_total: number;
  threshold: number;
  /** Additional M1 correct that would have reached the hard route (0 if reached). */
  needed: number;
  /** Scaled-score cap this section carried (easy route only). */
  cap: number | null;
};

export type PracticeTestReport = {
  session_id: string;
  user_id: string;
  form_id: string;
  /** Hard (band 4-5 only) challenge test; scaled score carries a caveat. */
  hard_mode?: boolean;
  /** Gauntlet: one timed hard Math module — raw score only, no 1600 scale. */
  gauntlet_mode?: boolean;
  /** Whether the session ran with module countdowns. */
  timed_mode?: boolean;
  submitted_at: number;
  rw: { correct: number; total: number; accuracy: number; scaled: number; answered?: number };
  math: { correct: number; total: number; accuracy: number; scaled: number; answered?: number };
  total: { correct: number; total: number; scaled: number };
  /** Which Module 2 path each section served on a routed (adaptive) test.
   *  Absent/null on linear (hard/gauntlet/legacy) sessions. */
  routes?: { rw?: "easy" | "hard"; math?: "easy" | "hard" } | null;
  /** Route coaching stats: Module-1 raw vs the routing threshold per section. */
  routing?: {
    rw?: SectionRouting;
    math?: SectionRouting;
  } | null;
  /** Plus-or-minus point uncertainty on the scaled score. */
  score_uncertainty: number;
  /**
   * Present ONLY when the student stopped before finishing. `total.scaled`
   * then scores every blank as wrong, which is true of a test that was taken
   * and false of one that was abandoned, so render `projected_total` instead.
   * Deliberately NOT `estimated_total`: that field's labels are PSAT-specific.
   */
  incomplete?: { answered: number; of: number };
  projected_total?: { point: number; low: number; high: number; plus_minus: number };
  /**
   * PSAT only. Present means: render THIS range, not total.scaled. Absent for
   * every other assessment, so the SAT presentation is untouched.
   */
  assessment?: "psat_nmsqt";
  estimated_total?: { point: number; low: number; high: number };
  skill_breakdown: Array<{
    module: string;
    skill_family: string;
    total: number;
    correct: number;
    missed: number;
    accuracy: number;
  }>;
  top_missed: Array<{
    module: string;
    skill_family: string;
    total: number;
    correct: number;
    missed: number;
    accuracy: number;
  }>;
  /** Per-question review: every answered item with the learner's choice + the why. */
  questions: PracticeTestReviewQuestion[];
};

export type PracticeTestReviewQuestion = {
  item_id: string;
  section: string;
  module: number;
  position: number;
  skill_family: string | null;
  stem: string;
  passage_text: string | null;
  context: string | null;
  /** Synthetic figure (quantitative-evidence / math-figure items), if any. */
  figure?: Figure | null;
  options: Array<{
    id: string;
    text: string;
    is_correct: boolean;
    rationale: string | null;
  }>;
  correct_option: string | null;
  chosen_answer: string | null;
  is_correct: boolean;
  marked_for_review: boolean;
  /** Time on this question in ms; absent on reports cached before this field shipped. */
  time_spent_ms?: number | null;
};

/** ACT report (test_type "act"): per-section 1-36 scores + composite/STEM, no
 *  routing. Scores are provisional (uncalibrated custom form). */
export type PracticeTestActReport = {
  test_type: "act";
  session_id: string;
  user_id: string;
  form_id: string;
  timed_mode?: boolean;
  submitted_at: number | null;
  sections: Array<{
    key: "english" | "math" | "reading" | "science";
    label: string;
    correct: number;
    total: number;
    accuracy: number;
    scaled: number;
  }>;
  /** round(mean(English, Math, Reading)); null until all three exist. */
  composite: number | null;
  /** round(mean(Math, Science)); null unless Science was taken. */
  stem: number | null;
  science_taken: boolean;
  provisional: boolean;
  skill_breakdown: Array<{
    section: string;
    skill_family: string;
    total: number;
    correct: number;
    missed: number;
    accuracy: number;
  }>;
  top_missed: PracticeTestActReport["skill_breakdown"];
  questions: PracticeTestReviewQuestion[];
};


/** AP Calculus report (test_type "apcalc"): a raw count, a per-part split and a
 *  unit breakdown. There is deliberately no score: Section I is half the exam,
 *  so a 1-5 cannot be inferred from it. `score_reporting` is echoed from the
 *  server so a client cannot render one by accident. */
export type PracticeTestApCalcReport = {
  test_type: "apcalc";
  session_id: string;
  user_id: string;
  form_id: string;
  exam: string;
  /** The literal, not `string`: typing it loosely would discard the one
   * compile-time guarantee this field exists to give. */
  score_reporting: "raw_only";
  timed_mode?: boolean;
  submitted_at: number | null;
  parts: Array<{
    key: string;
    label: string;
    correct: number;
    total: number;
    minutes: number;
    calculator: boolean;
  }>;
  correct: number;
  total: number;
  units: Array<{ unit: number; name: string; correct: number; total: number }>;
  /** False when the server could not tag every item; `units` is then empty. */
  units_complete?: boolean;
  skill_breakdown: PracticeTestActReport["skill_breakdown"];
  top_missed: PracticeTestActReport["skill_breakdown"];
  questions: PracticeTestReviewQuestion[];
};

/** Start an AP Calculus Section I practice test. */
export function startApCalcTest(formId?: string, timedMode?: boolean) {
  return fetchJson<{
    ok: true;
    session_id: string;
    test_type: "apcalc";
    exam: string;
    score_reporting: string;
    form: {
      form_id: string;
      name: string;
      sections: Array<{
        key: string;
        label: string;
        minutes: number;
        operational: number;
        calculator: boolean;
      }>;
    };
    started_at: number;
    current_section: string;
    timed_mode: boolean;
  }>("/practice-test/sessions", {
    method: "POST",
    body: JSON.stringify({
      test_type: "apcalc",
      ...(formId ? { form_id: formId } : {}),
      timed_mode: timedMode === true,
    }),
    auth: true,
  });
}

export function startPracticeTest(
  formId?: string,
  timedMode?: boolean,
  hardMode?: boolean,
  gauntletMode?: boolean,
) {
  // pt_started is NOT emitted here any more. It was, and it reported 29 students
  // against 67 for pt_first_item_shown: fewer starts than first questions, which
  // is impossible. Reaching the runner does not require this function. Resuming
  // navigates straight to /app/practice-test/run/:id and the ACT start has its
  // own path, so this counted one of the three ways a test begins. It now fires
  // server-side when the session row is created, which nothing can bypass.
  // See recordServerFunnel in apps/api/src/routes/telemetry.ts.
  const body: {
    form_id?: string;
    timed_mode?: boolean;
    hard_mode?: boolean;
    gauntlet_mode?: boolean;
  } = {};
  if (formId) body.form_id = formId;
  if (timedMode) body.timed_mode = true;
  if (hardMode) body.hard_mode = true;
  if (gauntletMode) body.gauntlet_mode = true;
  return fetchJson<{
    ok: true;
    session_id: string;
    form: PracticeTestForm;
    started_at: number;
    current_section: PracticeTestSection;
    current_module: number;
    current_index: number;
    timed_mode: boolean;
    hard_mode: boolean;
    module_started_at: number | null;
    /** True when the user has already taken every active form and this one is a replay. */
    exhausted_pool?: boolean;
  }>("/practice-test/sessions", {
    method: "POST",
    body: JSON.stringify(body),
    auth: true,
  });
}

/**
 * Start a full-length ACT test (Phase 0B internal harness). Posts test_type
 * "act"; the server assigns/loads an ACT form and returns the section list.
 */
/**
 * Start a PSAT/NMSQT or PSAT 10 practice test.
 *
 * No form id. The server assigns from the PSAT catalog, which is separate from
 * the SAT one on purpose: a SAT form served here would be built to a different
 * blueprint and scored on a different scale.
 */
export function startPsatTest(timedMode?: boolean) {
  return fetchJson<{
    ok: true;
    session_id: string;
    form: { form_id: string; name: string };
  }>("/practice-test/sessions", {
    method: "POST",
    body: JSON.stringify({ test_type: "psat", timed_mode: timedMode === true }),
    auth: true,
  });
}

export function startActTest(timedMode?: boolean, includeScience?: boolean, formId?: string) {
  const body: {
    test_type: "act";
    timed_mode?: boolean;
    include_science?: boolean;
    form_id?: string;
  } = {
    test_type: "act",
  };
  if (timedMode) body.timed_mode = true;
  if (includeScience === false) body.include_science = false;
  // Explicit form id starts a specific ACT form (e.g. an authentic Reading
  // section test); omitted -> the server auto-assigns the next full form.
  if (formId) body.form_id = formId;
  return fetchJson<{
    ok: true;
    session_id: string;
    test_type: "act";
    form: PracticeTestForm;
    started_at: number;
    current_section: PracticeTestSection;
    current_module: number;
    current_index: number;
    timed_mode: boolean;
    include_science: boolean;
    module_started_at: number | null;
  }>("/practice-test/sessions", {
    method: "POST",
    body: JSON.stringify(body),
    auth: true,
  });
}

export type PracticeTestFormSummary = {
  form_id: string;
  name: string;
  generation: string;
  /** "standard" or "hard" (the band 4-5-only challenge pool). */
  pool: string;
};

/** The active form catalog (used to detect "you've taken them all"). */
export function fetchPracticeTestForms() {
  return fetchJson<{ forms: PracticeTestFormSummary[]; generated_at: string | null }>(
    "/practice-test/forms",
    { auth: true },
  );
}

export type PracticeTestSessionListEntry = {
  session_id: string;
  form_id: string;
  status: "in_progress" | "submitted" | "abandoned";
  started_at: number;
  submitted_at: number | null;
  rw_correct: number | null;
  rw_total: number | null;
  math_correct: number | null;
  math_total: number | null;
  rw_scaled: number | null;
  math_scaled: number | null;
  total_scaled: number | null;
  timed_mode: boolean;
  hard_mode: boolean;
  /**
   * For an in-progress TIMED session: has its section clock already run out?
   *
   * `null` means not applicable or not knowable (untimed, finished, or a form
   * snapshot the server could not read). Only `true` may drive a claim, and
   * only `false` may show a Resume button; treating null as either ends a test
   * or offers an impossible one.
   */
  time_expired?: boolean | null;
  /**
   * What tells two unfinished tests of the SAME exam apart. Three unfinished
   * SAT tests are identical strings without these, and the form id does not
   * help: "pt-2026-09-08b-01" means nothing to a student. Where they are, how
   * far they got, and when.
   *
   * Optional because an older API does not send them, and a missing progress
   * count must degrade to "no detail" rather than to "0 of 98 answered".
   */
  answered?: number;
  /**
   * Paused at the section break: the clock is stopped and the whole next module
   * is still waiting. Distinct from merely unfinished, and the difference is
   * what Home has to say to keep the break's promise.
   */
  on_break?: boolean;
  current_section?: string | null;
  current_module?: number | null;
  test_type?: string;
  /** One timed hard Math module (22q / 35min); no 1600-scale score. */
  gauntlet_mode?: boolean;
};

/** The student discards an unfinished test. Not scored; see the route's note. */
export function discardPracticeTestSession(sessionId: string) {
  return fetchJson<{ ok: true; session_id: string; status: "abandoned" }>(
    `/practice-test/sessions/${encodeURIComponent(sessionId)}`,
    { method: "DELETE", auth: true },
  );
}

export function fetchPracticeTestSessions() {
  return fetchJson<{ sessions: PracticeTestSessionListEntry[] }>(
    "/practice-test/sessions",
    { auth: true },
  );
}

export function fetchPracticeTestState(sessionId: string) {
  return fetchJson<PracticeTestStateResponse>(
    `/practice-test/sessions/${sessionId}`,
    { auth: true },
  ).then(normalizeResponse);
}

export function submitPracticeTestResponse(
  sessionId: string,
  body: {
    item_id: string;
    // A-D for multiple choice, or a typed grid-in answer (the API accepts both).
    chosen_answer: string | null;
    is_correct: boolean | null;
    time_spent_ms: number | null;
    marked_for_review?: boolean;
    position: number;
  },
) {
  return fetchJson<{ ok: true }>(
    `/practice-test/sessions/${sessionId}/respond`,
    {
      method: "POST",
      body: JSON.stringify(body),
      auth: true,
    },
  );
}

export function nextPracticeTestModule(sessionId: string) {
  return fetchJson<{
    ok: true;
    next_section: PracticeTestSection;
    next_module: number | null;
    on_break?: boolean;
    break_minutes?: number | null;
  }>(`/practice-test/sessions/${sessionId}/next-module`, {
    method: "POST",
    body: JSON.stringify({}),
    auth: true,
  });
}

/**
 * Leave the section break and start the module's clock.
 *
 * Separate from nextPracticeTestModule because the break exists precisely so that
 * entering a module and starting its clock are two different moments. Idempotent
 * server-side, so a double tap cannot buy a second full module.
 */
export function startPracticeTestModule(sessionId: string) {
  return fetchJson<{ ok: true; started: boolean }>(
    `/practice-test/sessions/${sessionId}/start-module`,
    { method: "POST", body: JSON.stringify({}), auth: true },
  );
}

export function submitPracticeTest(sessionId: string) {
  return fetchJson<{
    ok: true;
    rw_correct: number;
    rw_total: number;
    math_correct: number;
    math_total: number;
    submitted_at: number;
  }>(`/practice-test/sessions/${sessionId}/submit`, {
    method: "POST",
    body: JSON.stringify({}),
    auth: true,
  });
}

export function fetchPracticeTestReport(sessionId: string) {
  return fetchJson<PracticeTestReport | PracticeTestActReport | PracticeTestApCalcReport>(
    `/practice-test/sessions/${sessionId}/report`,
    { auth: true },
  );
}

/** Discriminate the report union at the call site. */
export function isActReport(
  r: PracticeTestReport | PracticeTestActReport | PracticeTestApCalcReport,
): r is PracticeTestActReport {
  return (r as PracticeTestActReport).test_type === "act";
}

/**
 * Requires BOTH the exam and the reporting mode. A response claiming apcalc with
 * some other mode is not a report this view knows how to render, and rendering
 * it anyway is how a score we do not compute would reach a screen.
 */
export function isApCalcReport(
  r: PracticeTestReport | PracticeTestActReport | PracticeTestApCalcReport,
): r is PracticeTestApCalcReport {
  const c = r as PracticeTestApCalcReport;
  return c.test_type === "apcalc" && c.score_reporting === "raw_only";
}

// --- Score trajectory (Feature 1) -----------------------------

export type ScoreHistoryPoint = {
  session_id: string;
  submitted_at: number;
  rw_scaled: number;
  math_scaled: number;
  total_scaled: number;
  timed_mode: boolean;
  /** Hard (band 4-5 only) challenge test — scored on the same curve but not
   * comparable to a mixed-difficulty SAT; charts mark these points. */
  hard_mode: boolean;
};

/** The user's completed practice tests over time, oldest → newest. */
export function fetchScoreHistory() {
  return fetchJson<{ tests: ScoreHistoryPoint[] }>(
    "/practice-test/score-history",
    { auth: true },
  );
}

// --- Diagnostic placement (Feature 2) -------------------------

export type DiagnosticSession = {
  items: PackItem[];
  modules: string[];
};

/** ~15 cross-module MC items to seed the skill radar for a cold-start user. */
export function fetchDiagnosticSession() {
  return fetchJson<DiagnosticSession>("/packs/diagnostic/session", {
    auth: true,
  }).then(normalizeResponse);
}

/**
 * Persist a completed diagnostic result for predicted-band curve calibration
 * (paired offline to the user's later practice-test scaled score). Best-effort.
 */
export function submitDiagnosticResult(body: {
  rw_correct: number;
  rw_total: number;
  math_correct: number;
  math_total: number;
  predicted_low: number | null;
  predicted_mid: number | null;
  predicted_high: number | null;
}) {
  return fetchJson<{ ok: true }>("/packs/diagnostic/result", {
    method: "POST",
    body: JSON.stringify(body),
    auth: true,
  });
}

// --- Item flagging (Phase 9) -----------------------------------

export type FlagType = "confusing" | "unfair" | "off_topic" | "looks_great";

export function flagItem(
  itemId: string,
  body: { attempt_id: string; flag_type: FlagType },
) {
  return fetchJson<{ ok: true; was_new: boolean }>(
    `/items/${encodeURIComponent(itemId)}/flag`,
    {
      method: "POST",
      body: JSON.stringify(body),
      auth: true,
    },
  );
}

// --- General feedback (floating feedback bubble) ---------------

export type FeedbackCategory = "question" | "confused" | "bug" | "idea" | "other";

export function submitFeedback(body: {
  category: FeedbackCategory;
  message: string;
  path?: string;
  item_id?: string;
  client?: string;
}) {
  return fetchJson<{ ok: true; id: string }>("/feedback", {
    method: "POST",
    body: JSON.stringify(body),
    auth: true,
  });
}

// --- Config (Desmos calculator key) ---------------------------

/** Fetch the Desmos graphing-calculator API key (authed). 503 if unconfigured. */
export function fetchDesmosKey() {
  return fetchJson<{ apiKey: string }>("/config/desmos", { auth: true });
}

export type FeedbackReplyRow = {
  id: string;
  message: string;
  reply: string;
  replied_at: number;
};

/** Replies to this learner's feedback that they have not dismissed yet. */
export function getFeedbackReplies(): Promise<{ replies: FeedbackReplyRow[] }> {
  return fetchJson<{ replies: FeedbackReplyRow[] }>("/feedback/replies", { auth: true });
}

/** Stop showing one reply. */
export function markFeedbackReplySeen(id: string): Promise<{ ok: true }> {
  return fetchJson<{ ok: true }>(`/feedback/replies/${encodeURIComponent(id)}/seen`, {
    method: "POST",
    auth: true,
  });
}

// ---------- Post-test repair set ----------

/**
 * The five questions a finished practice test says are worth fixing.
 * Plan: docs/PLAN-2026-09-16-post-test-retention.md.
 *
 * `total` IS THE QUESTION COUNT. A set is usually five and can be three when the
 * bank is thin in that skill, so no screen may hardcode "5 questions" or the
 * minutes that follow from it. Every surface renders from this number.
 */
export type RepairSet = {
  id: string;
  session_id: string;
  module: string;
  skill_family: string;
  /** What the test itself showed for this skill, frozen when the set was made. */
  test_correct: number;
  test_total: number;
  done: number;
  total: number;
  complete: boolean;
  created_at: number;
  completed_at: number | null;
};

export type RepairResult = {
  repair_set: RepairSet;
  test_correct: number;
  test_total: number;
  repair_correct: number;
  repair_answered: number;
};

/** The unfinished set, for the Home card. Resolves to null when there is none. */
export function fetchOpenRepairSet() {
  return fetchJson<{ repair_set: RepairSet | null }>("/repair-sets/open", {
    auth: true,
  }).then((r) => r.repair_set);
}

/**
 * The set a given test created, for the post-test screen. Null is a real answer
 * ("you missed nothing twice"), not an error, so this never rejects on absence.
 */
export function fetchRepairSetForTest(sessionId: string) {
  return fetchJson<{ repair_set: RepairSet | null }>(
    `/repair-sets/by-session/${sessionId}`,
    { auth: true },
  ).then((r) => r.repair_set);
}

/** The questions themselves, served and shuffled like any other drill session. */
export function fetchRepairSession(setId: string) {
  return fetchJson<{ repair_set: RepairSet; items: PackItem[] }>(
    `/repair-sets/${setId}/session`,
    { auth: true },
  ).then(normalizeResponse);
}

/** The closing comparison, once the set is finished. */
export function fetchRepairResult(setId: string) {
  return fetchJson<RepairResult>(`/repair-sets/${setId}/result`, { auth: true });
}
